import { format } from '@if/core/date'
import DownloadIcon from '@mui/icons-material/Download'
import GetAppIcon from '@mui/icons-material/GetApp'
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import {
  BulkExportTaskListItemDto,
  downloadBulkDocumentExportTaskErrorsCsv,
} from '@src/data/api/bulk-document-export/bulk-document-export-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const statusColorMap: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  Pending: 'default',
  Running: 'info',
  Completed: 'success',
  Failed: 'error',
  Terminated: 'warning',
  Suspended: 'warning',
}

interface BuildColumnsOptions {
  onOpenErrors: (anchor: HTMLElement, row: BulkExportTaskListItemDto) => void
}

export const useBulkExportTaskColumns = (
  options: BuildColumnsOptions,
): GridColDef<BulkExportTaskListItemDto>[] => {
  const { t } = useTranslation()
  const { onOpenErrors } = options

  return useMemo<GridColDef<BulkExportTaskListItemDto>[]>(
    () => [
      {
        field: 'createdAt',
        headerName: t('bulkDocumentExport.tasks.columns.createdAt'),
        minWidth: 160,
        sortable: false,
        renderCell: (params) =>
          params.row.createdAt ? format(new Date(params.row.createdAt), 'yyyy-MM-dd HH:mm') : '',
      },
      {
        field: 'financingProgramId',
        headerName: t('bulkDocumentExport.tasks.columns.program'),
        minWidth: 130,
        sortable: false,
      },
      {
        field: 'runtimeStatus',
        headerName: t('bulkDocumentExport.tasks.columns.status'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={t(`bulkDocumentExport.status.${params.row.runtimeStatus}`, {
              defaultValue: params.row.runtimeStatus,
            })}
            color={statusColorMap[params.row.runtimeStatus] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'progress',
        headerName: t('bulkDocumentExport.tasks.columns.progress'),
        minWidth: 110,
        sortable: false,
        renderCell: (params) =>
          params.row.total > 0 ? `${params.row.staged}/${params.row.total}` : '–',
      },
      {
        field: 'errorCount',
        headerName: t('bulkDocumentExport.tasks.columns.errors'),
        minWidth: 110,
        sortable: false,
        renderCell: (params) => {
          if (params.row.errorCount === 0) return <span>0</span>
          return (
            <Chip
              label={params.row.errorCount}
              size="small"
              color="error"
              variant="outlined"
              clickable
              onClick={(e) => onOpenErrors(e.currentTarget, params.row)}
            />
          )
        },
      },
      {
        field: 'completedAt',
        headerName: t('bulkDocumentExport.tasks.columns.completedAt'),
        minWidth: 160,
        sortable: false,
        renderCell: (params) =>
          params.row.completedAt ? format(new Date(params.row.completedAt), 'yyyy-MM-dd HH:mm') : '',
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={t('bulkDocumentExport.tasks.downloadZip')}>
            <span>
              <IconButton
                size="small"
                disabled={!params.row.zipUrl}
                component="a"
                href={params.row.zipUrl ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [t, onOpenErrors],
  )
}

interface BulkExportErrorBreakdownPopoverProps {
  anchorEl: HTMLElement | null
  row: BulkExportTaskListItemDto | null
  onClose: () => void
}

/**
 * Toggleable box anchored under the errorCount chip. Lists each error category and its count
 * (no loanIds — keeps the popover short even on runs with thousands of errors). A
 * "Download CSV" button exposes the full per-loanId dump.
 */
export const BulkExportErrorBreakdownPopover = ({
  anchorEl,
  row,
  onClose,
}: BulkExportErrorBreakdownPopoverProps): React.ReactNode => {
  const { t } = useTranslation()
  const open = anchorEl !== null && row !== null
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)

  const handleDownloadCsv = async () => {
    if (!row) return
    setIsDownloadingCsv(true)
    try {
      await downloadBulkDocumentExportTaskErrorsCsv(row.commandId)
    } catch (error) {
      reportErrorToConsole(error)
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, maxWidth: 460, maxHeight: 520, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {t('bulkDocumentExport.tasks.errorBreakdown.title')}
          </Typography>
          <Button
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadCsv}
            disabled={isDownloadingCsv || !row}
          >
            {t('bulkDocumentExport.tasks.errorBreakdown.downloadCsv')}
          </Button>
        </Box>

        <List dense disablePadding>
          {row?.errorCountsByCategory.map((category) => (
            <ListItem key={category.category} disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                slotProps={{ primary: { variant: 'body2' } }}
                primary={
                  <>
                    {t(`bulkDocumentExport.errorTypes.${category.category}`, { defaultValue: category.category })}
                    {' '}<strong>({category.count})</strong>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Popover>
  )
}
